import React, { Dispatch, SetStateAction } from "react";
import { AxiosError } from "axios";
import { toast, ToastPosition } from "react-toastify";
import it from "./dictionaries/it.json";
import en from "./dictionaries/en.json";
import fr from "./dictionaries/fr.json";
import { ErrorResponse } from "../Models/Error";
import { isEmpty } from "lodash";
import timezones from "./timezones.json";

const dictionaries: Record<string, any> = { it, en, fr };

/**
 * Returns the dictionary for the user's selected language.
 *
 * This function retrieves the preferred language from `localStorage` or
 * `navigator.language`. If the language is not supported, English is used as a fallback.
 *
 * @returns {object} The dictionary corresponding to the user's language.
 */
export const getDictionary = (): Record<string, any> => {
  const userLanguage =
    localStorage.getItem("language") || navigator.language || "en";
  const normalizedLanguage = userLanguage.split("-")[0];
  return dictionaries[normalizedLanguage] || dictionaries["en"];
};

/**
 * Retrieves the error message associated with a specific field in an HTTP error response.
 *
 * This function checks if the provided error is an instance of `AxiosError` and,
 * if so, extracts the error message for the specified field from the API response.
 *
 * @param {unknown} error - The error received from the HTTP request.
 * @param {string} field - The name of the field for which to retrieve the error message.
 * @returns {string | undefined} The error message for the specified field, or `undefined` if not available.
 */
export const getFieldError = (
  error: unknown,
  field: string
): string | undefined => {
  if (error && typeof error === "object") {
    //BE error
    if ("isAxiosError" in error) {
      return (error as AxiosError<{ [key: string]: string }>).response?.data?.[
        field
      ];
    }
    //FE error
    return (error as { response: { data: Record<string, string> } }).response
      .data[field];
  }

  return undefined;
};

/**
 * Displays an error toast notification based on the provided error object.
 *
 * This function processes different types of errors and extracts meaningful messages
 * to display as toast notifications. It handles network errors, HTTP status codes (400, 404, 500),
 * and any unexpected errors. If a dictionary object is provided, it uses localized error messages.
 *
 * @param err - The error object, which can be of any type (preferably an object with `code` and `response` properties).
 * @param dictionary - An optional dictionary object containing localized messages. Defaults to the current language from `dictionaries`.
 *
 * @example
 * ```ts
 * try {
 *   await fetchData();
 * } catch (error) {
 *   callErrorToast(error);
 * }
 * ```
 */
export const callErrorToast = (err: unknown) => {
  let message = "";
  const dictionary = getDictionary();
  if (typeof err === "object" && err !== null) {
    const errorObj = err as {
      code?: string;
      response?: { status?: number; data?: any; message?: string };
    };

    if (errorObj.code === "ERR_NETWORK") {
      message = dictionary.messages.network_error;
    } else {
      switch (errorObj.response?.status) {
        case 400:
          message =
            "400 - Bad Request - " +
            (errorObj.response.data
              ? JSON.stringify(errorObj.response.data)
              : errorObj.response.message || "");
          break;
        case 404:
          message = "404 - Not Found";
          break;
        case 500:
          message = "500 - Internal Server Error";
          break;
        default:
          message = `Internal Web Error: ${JSON.stringify(err)}`;
      }
    }
  } else {
    message = `Unexpected Error: ${JSON.stringify(err)}`;
  }

  toast.error(message, {
    position: toast.POSITION.TOP_CENTER,
  });
};

/**
 * Displays an error toast notification with the provided message.
 *
 * This function displays an error toast notification on the screen with the given message.
 * The toast will appear at a specific position on the screen, which can be customized.
 * By default, the toast will be displayed at the top-center of the screen.
 *
 * Note: Despite the function name suggesting it's for success messages, this function
 * uses `toast.error` internally, meaning it displays an error toast notification.
 * Consider renaming the function if the intention is to show success notifications.
 *
 * @param message - The error message to display in the toast notification.
 * @param position - The position where the toast notification should appear.
 *   Defaults to `toast.POSITION.TOP_CENTER` if not provided.
 *
 * @example
 * ```ts
 * callSuccessToast("Something went wrong while processing your request.");
 * ```
 */
export const callSuccessToast = (
  message: string,
  position: ToastPosition = toast.POSITION.TOP_CENTER
) => {
  toast.success(message, {
    position,
  });
};

/**
 * Generates a color code (hex format) based on a string input.
 * This function creates a unique color hash by iterating through the string's characters
 * and applies bit manipulation to convert the string into a hex color code.
 *
 * @param str - The input string used to generate the color.
 * @returns A string representing the generated color in hex format (e.g., "#RRGGBB").
 *
 * @example
 * stringToColor("hello"); // Example output: "#7b7b7b"
 */
export const transformStringToColor = (str = ""): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ("00" + value.toString(16)).slice(-2);
  }

  return color;
};

/**
 * Returns the most readable text color (#000 or #fff) based on the background,
 * using WCAG contrast ratio and a customizable minimum contrast threshold.
 *
 * @param bgHex - The background color in hex format (e.g., "#70AFE2")
 * @param minContrast - Optional. Minimum desired contrast ratio (default: 4.5)
 * @param fallback - Optional. Color to use if both black and white fail (default: "#fff")
 * @returns "#000" or "#fff" depending on contrast, or fallback color
 */
export const getSmartTextColor = (
  bgHex: string,
  minContrast = 4.5,
  fallback: "#000" | "#fff" = "#fff"
): "#000" | "#fff" => {
  const r = parseInt(bgHex.slice(1, 3), 16);
  const g = parseInt(bgHex.slice(3, 5), 16);
  const b = parseInt(bgHex.slice(5, 7), 16);

  const getLuminance = (r: number, g: number, b: number): number => {
    const a = [r, g, b].map((v) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
  };

  const getContrast = (lum1: number, lum2: number) =>
    (Math.max(lum1, lum2) + 0.05) / (Math.min(lum1, lum2) + 0.05);

  const bgLum = getLuminance(r, g, b);
  const contrastWhite = getContrast(bgLum, 1); // white
  const contrastBlack = getContrast(bgLum, 0); // black

  if (contrastWhite >= minContrast && contrastWhite >= contrastBlack) {
    return "#fff";
  } else if (contrastBlack >= minContrast) {
    return "#000";
  }

  // Fallback if neither passes the threshold
  return fallback;
};

/**
 * Removes properties with falsy values from an object.
 *
 * This function returns a new copy of the object with all properties that have
 * falsy values (`null`, `undefined`, `false`, `0`, `""`, etc.) removed.
 *
 * @template T - A generic type representing the shape of the input object.
 * @param {T} obj - The input object of any type extending `Record<string, any>`.
 * @returns {T} A new object with falsy properties removed.
 *
 * @example
 * const person = { name: "John", age: 30, city: "" };
 * const cleanedPerson = clearObject(person);
 * console.log(cleanedPerson); // { name: "John", age: 30 }
 */
export const cleanObject = <T extends Record<string, any>>(obj: T): T => {
  const newObj = { ...obj };
  for (const prop in newObj) {
    if (
      newObj[prop] === null ||
      newObj[prop] === undefined ||
      newObj[prop] === ""
    ) {
      delete newObj[prop];
    }
  }
  return newObj;
};

/**
 * The `removeError` function is used to remove a specific property from the `data` object of an error response.
 * If the property exists in the error's data, it will be removed. If removing the property leaves the data object
 * empty, the error state will be cleared. If there are still other properties left in the `data`, the error state will
 * be updated with the modified `data`.
 *
 * This function is commonly used in error handling scenarios where you might want to remove a specific error detail
 * dynamically (e.g., after user acknowledgment or resolution of a particular issue), and update the error state accordingly.
 *
 * @param property - The property of type `T` to be removed from the error's `data`. It should be a valid key of `T`.
 * @param error - The current error response, which contains the error `data`. If `null`, no action is performed.
 * @param prevError - A `RefObject` holding the previous error state, which is used to store the modified error data after the operation.
 * @param setError - A function to update the error state, either to the modified error data or to `null` if the data is empty.
 *
 * @returns void - This function does not return anything. It modifies the error state directly.
 *
 * @example
 * // Example usage:
 * removeError('name', errorResponse, prevError, setError);
 *
 * // This will remove the 'name' property from the error's data, and if there are no other properties left,
 * // it will clear the error state entirely. Otherwise, it will update the error state with the modified `data`.
 */
export const removeError = <T>(
  property: keyof T,
  error: ErrorResponse<T> | null,
  prevError: React.RefObject<ErrorResponse<T> | null>,
  setError: Dispatch<SetStateAction<ErrorResponse<T> | null>>
): void => {
  if (error?.response?.data) {
    const { data } = error.response;

    if (property in data) {
      // const { [property]: _, ...newData } = data;
      const newData = { ...data };
      delete newData[property];

      if (isEmpty(newData)) {
        setError(null);
        prevError.current = null;
      } else {
        setError({ response: { data: newData } });
        prevError.current = { response: { data: data } };
      }
    }
  }
};

/**
 * Returns a combined list of all available timezones.
 *
 * Iterates over the `timezones` array and merges all the `options`
 * from each timezone group into a single flat array.
 *
 * @returns {any[]} An array containing all timezone options from `timezones`.
 */
export const getAllTimezones = (): any[] => {
  let allZones: any[] = [];
  timezones.forEach((t) => {
    allZones = allZones.concat(t.options);
  });
  return allZones;
};
